import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Lock, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | invalid | form | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    apiFetch(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then((r) => setState(r.valid ? "form" : "invalid"))
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setErrorText("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (password !== confirm) {
      setErrorText("Le password non coincidono.");
      return;
    }
    setLoading(true);
    setErrorText("");
    try {
      await apiFetch("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
      setState("done");
    } catch (error) {
      setErrorText(error.message || "Operazione non riuscita. Riprova.");
      if ((error.message || "").includes("Link non valido")) setState("invalid");
    } finally {
      setLoading(false);
    }
  };

  const inputWrap =
    "flex min-h-[50px] items-center gap-2.5 rounded-2xl border border-border bg-card px-4";
  const btn =
    "mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold tracking-wide text-primary-foreground hover:opacity-90 disabled:opacity-60";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-lg">
        <img src="/cargoflow-logo.jpg" alt="CargoFlow" className="mx-auto h-16 w-auto object-contain" />
        {state === "checking" ? (
          <div className="mt-8 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : null}

        {state === "invalid" ? (
          <div className="mt-6 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
            <h1 data-testid="reset-invalid-title" className="mt-3 text-2xl font-extrabold text-foreground">
              Link non valido o scaduto
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Il link di reset può essere usato una sola volta e scade dopo 60 minuti. Richiedi un nuovo link dalla
              schermata di accesso.
            </p>
            <Link to="/login" data-testid="reset-login-link" className={btn} style={{ display: "flex" }}>
              TORNA AL LOGIN
            </Link>
          </div>
        ) : null}

        {state === "form" ? (
          <form onSubmit={submit} className="mt-6">
            <h1 className="text-center text-2xl font-extrabold text-foreground">Imposta una nuova password</h1>
            <p className="mt-2 text-center text-sm text-muted-foreground">Scegli una password di almeno 8 caratteri.</p>
            <label className="mt-5 block text-sm font-semibold text-card-foreground">Nuova password</label>
            <div className={`mt-1 ${inputWrap}`}>
              <Lock className="h-5 w-5 text-muted-foreground" />
              <input
                data-testid="reset-password"
                type="password"
                placeholder="Minimo 8 caratteri"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 flex-1 bg-transparent text-base text-foreground outline-none"
              />
            </div>
            <label className="mt-3 block text-sm font-semibold text-card-foreground">Conferma password</label>
            <div className={`mt-1 ${inputWrap}`}>
              <Lock className="h-5 w-5 text-muted-foreground" />
              <input
                data-testid="reset-confirm"
                type="password"
                placeholder="Ripeti la password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-12 flex-1 bg-transparent text-base text-foreground outline-none"
              />
            </div>
            {errorText ? (
              <p data-testid="reset-error" className="mt-3 text-sm font-semibold text-destructive">
                {errorText}
              </p>
            ) : null}
            <button type="submit" data-testid="reset-submit" disabled={loading} className={btn}>
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {loading ? "Aggiornamento..." : "AGGIORNA PASSWORD"}
            </button>
          </form>
        ) : null}

        {state === "done" ? (
          <div className="mt-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[hsl(var(--success))]" />
            <h1 data-testid="reset-done-title" className="mt-3 text-2xl font-extrabold text-foreground">
              Password aggiornata
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              La tua password è stata cambiata con successo. Accedi con la nuova password.
            </p>
            <Link to="/login" data-testid="reset-done-login" className={btn} style={{ display: "flex" }}>
              VAI AL LOGIN
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
